import useGetSingleForm from "~/hooks/forms/useGetSingleForm";
import useData, { type UseDataReturn } from "~/hooks/useData";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import { clearAllCollectionCache } from "~/lib/cache/clearCache";
import type { FormSubmission } from "~/types/collections";
import type {
  DocumentNameField,
  FormField,
  FormFields,
  FormState,
} from "~/types/forms";

interface Args {
  submissionId?: string;
  serialLoading?: boolean;
}

// Helper function to find DocumentName field from form fields
const findDocumentNameField = (
  fields?: FormField[] | null
): DocumentNameField | null => {
  if (!fields) return null;

  for (const field of fields) {
    if (field.blockType === "documentName") {
      return field as DocumentNameField;
    }
  }

  return null;
};

// Helper function to format document name with prepend/append
const formatDocumentName = (
  originalName: string,
  prepend?: string | null,
  append?: string | null
): string => {
  let formattedName = originalName;

  if (prepend) {
    formattedName = prepend + formattedName;
  }

  if (append) {
    formattedName = formattedName + append;
  }

  return formattedName;
};

export const flattenFormSubmission = (
  formState: FormState,
  formFields: FormFields
) => {
  let flattenedFormState = formState;

  // Check if data contains userDefinedDocumentName and apply formatting
  if (flattenedFormState?.userDefinedDocumentName && formFields) {
    const documentNameField = findDocumentNameField(formFields);

    if (documentNameField) {
      const formattedName = formatDocumentName(
        typeof flattenedFormState.userDefinedDocumentName === "string"
          ? flattenedFormState.userDefinedDocumentName
          : "",
        documentNameField.prepend,
        documentNameField.append
      );

      flattenedFormState = {
        ...flattenedFormState,
        userDefinedDocumentName: formattedName,
      };
    }
  }

  return flattenedFormState;
};

// This hook flattens and unflattens the form submission data to be compatible with the form stepper.
// The backend requires flat data. Frontend needs pages for each step.
// Note: When a user is not interacting with the backend (i.e. tempId) the data is not flattened.
// Local Storage takes data as pages.

type UseGetSingleFormSubmissionsReturn = Omit<
  UseDataReturn<FormSubmission>,
  "data" | "createItem" | "updateItem" | "isLoading"
> & {
  data: FormSubmission | undefined;
  createItem: (data: any) => Promise<any>;
  updateItem: (data: any) => Promise<any>;
  isLoading: boolean;
};

const useGetSingleFormSubmissions = (
  args: Args = {}
): UseGetSingleFormSubmissionsReturn => {
  const { isLoading: userLoading } = useCurrentUser();

  const {
    createItem: createFormSubmission,
    updateItem: updateFormSubmission,
    data: submissionData,
    isLoading: isSubmissionLoading,
    ...rest
  } = useData<FormSubmission>({
    serialLoading: args.serialLoading || userLoading,
    collection: "form-submissions",
    id: args.submissionId,
  });

  const { data: formData, isLoading: isFormLoading } = useGetSingleForm({
    templateId:
      typeof submissionData?.template === "object" &&
      submissionData?.template !== null
        ? (submissionData.template as { id: string }).id
        : undefined,
    serialLoading: isSubmissionLoading,
  });

  const isLoading = isFormLoading || isSubmissionLoading;

  const stateToPages = isSubmissionLoading ? {} : submissionData?.submission;

  const createItem = async (data: any) => {
    const { submission, ...rest } = data;

    const flattenedFormState = flattenFormSubmission(
      submission,
      formData?.fields ?? []
    );

    const res = await createFormSubmission({
      submission: flattenedFormState,
      ...rest,
    });

    // Invalidate caches so document lists reflect new form submission
    clearAllCollectionCache("editable-documents");
    clearAllCollectionCache("documents");
    clearAllCollectionCache("form-submissions");

    return res;
  };

  const updateItem = async (data: any) => {
    const { submission, ...rest } = data;

    const res = await updateFormSubmission({
      submission,
      ...rest,
    });

    // Reset caches when a form submission is updated.
    // This ensures the latest form submission data is displayed.
    clearAllCollectionCache("editable-documents");
    clearAllCollectionCache("documents");
    clearAllCollectionCache("form-submissions");

    return res;
  };

  return {
    createItem,
    updateItem,
    isLoading,
    data: submissionData
      ? {
          ...submissionData,
          submission: stateToPages || {},
        }
      : undefined,
    ...rest,
  };
};

export default useGetSingleFormSubmissions;
